import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Box, Stack } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { z } from 'zod';
import { ProtectedComponent } from '@components/ProtectedComponent';
import { FallbackType, UserRole } from '@shared-types/auth.types';
import { PageHeader } from '@components/ui/PageHeader';
import { SearchBarWithSuggestions } from '@features/search/components/SearchBarWithSuggestions';
import { SearchResultsGrid } from '@features/search/components/SearchResultsGrid';
import { globalSearchQueryOptions } from '@api/search/searchQueries';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { GlobalSearchResponse } from '@shared-types/search.types';

const searchParamsSchema = z.object({
  q: z.string().default(''),
});

export const Route = createFileRoute('/_authed/search/')({
  component: SearchResultsPage,
  validateSearch: searchParamsSchema,
});

function SearchResultsPage() {
  const navigate = useNavigate({from: Route.fullPath});
  const {q: query} = Route.useSearch();

  const shouldSearch = query.trim().length >= 2;

  const searchQuery = useQuery(globalSearchQueryOptions(query, shouldSearch));

  const results: GlobalSearchResponse = useMemo(() => searchQuery.data || {
    customers: [],
    systems: [],
    licenses: [],
    hardwareUnits: [],
    totalCustomers: 0,
    totalSystems: 0,
    totalLicenses: 0,
    totalHardwareUnits: 0,
  }, [searchQuery.data]);

  const totalResults: number = results.totalCustomers + results.totalSystems + results.totalLicenses + results.totalHardwareUnits;
  const isLoading: boolean = searchQuery.isFetching;
  const error: Error | null = searchQuery.error;

  const handleSearch = useCallback(
    (newQuery: string) => {
      navigate({
        search: {q: newQuery},
      });
    },
    [navigate]
  );

  const handleRefresh = useCallback(() => {
    // Trigger refetch by navigating to same route
    navigate({
      search: (prev) => ({...prev}),
      replace: true,
    });
  }, [navigate]);

  const getSubtitle = () => {
    if (!query || query.trim().length < 2) {
      return 'Enter a search term to find systems and customers';
    }
    return `Search results for "${query}"`;
  };

  return (
    <ProtectedComponent
      requiredRole={UserRole.Viewer}
      fallbackType={FallbackType.Page}
      fallbackTitle="Search"
      fallbackAction="search for systems and customers"
    >
      <Box>
        <Stack gap="lg">
          <PageHeader
            title="Search"
            subtitle={getSubtitle()}
            backButton={false}
            actions={[
              {
                label: 'Refresh',
                icon: IconRefresh,
                onClick: handleRefresh,
                loading: isLoading,
                ariaLabel: 'refresh-search-results',
                color: 'green',
              },
            ]}
          >
            <Box>
              <SearchBarWithSuggestions
                initialValue={query}
                onSearch={handleSearch}
              />
            </Box>
          </PageHeader>

          <SearchResultsGrid
            results={results}
            totalResults={totalResults}
            isLoading={isLoading}
            error={error}
            query={query}
          />
        </Stack>
      </Box>
    </ProtectedComponent>
  );
}
